import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faSpinner,
  faAngleLeft,
  faAngleRight,
  faSearch,
  faHistory,
  faDownload,
  faXmark,
  faSyncAlt
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { BASE_URL } from "../../config";

const Reports = () => {
  const [activeTab, setActiveTab] = useState("reports"); // 'reports' or 'history'
  const [reportType, setReportType] = useState("ar_aging");
  const [filters, setFilters] = useState({
    corporate: [],
    dateFrom: "",
    dateTo: "",
    paymentStatus: "",
    billedType: "",
  });
  const [corporateSearch, setCorporateSearch] = useState("");
  const [filteredCorporations, setFilteredCorporations] = useState([]);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [historyData, setHistoryData] = useState([]);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotalEntries, setHistoryTotalEntries] = useState(0);

  const phpFormatter = new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const fetchCorporations = async (search = "") => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/reports/corporations`, {
        params: { search },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setFilteredCorporations(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch corporations.", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCorporateSearch = (e) => {
    const value = e.target.value;
    setCorporateSearch(value);
    fetchCorporations(value);
  };

  const handleCorporateSelect = (corp) => {
    if (filters.corporate.some((c) => c.id === corp.id)) {
      toast.error(`"${corp.name}" is already selected.`, { duration: 3000 });
      setCorporateSearch("");
      setFilteredCorporations([]);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      corporate: [...prev.corporate, corp],
    }));
    setCorporateSearch("");
    setFilteredCorporations([]);
  };

  const handleRemoveCorporate = (corpId) => {
    setFilters((prev) => ({
      ...prev,
      corporate: prev.corporate.filter((c) => c.id !== corpId),
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/reports`, {
        params: {
          report_type: reportType,
          corporate: filters.corporate.map((c) => c.id),
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          payment_status: filters.paymentStatus,
          billed_type: filters.billedType,
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setData(response.data.data);
      setColumns(response.data.columns);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error("Failed to fetch report.", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchExportHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/reports/export-history`, {
        params: {
          report_type: reportType,
          page: historyCurrentPage,
          per_page: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setHistoryData(response.data.data);
      setHistoryTotalEntries(response.data.pagination.total_records);
      setHistoryTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error("Failed to fetch export history.", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/reports/export`,
        {
          report_type: reportType,
          export_format: exportFormat,
          corporate: filters.corporate.map((c) => c.id),
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          payment_status: filters.paymentStatus,
          billed_type: filters.billedType,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      const link = document.createElement("a");
      link.href = response.data.download_url;
      link.download = response.data.original_file_name;
      link.click();
      toast.success("Report exported successfully.", { duration: 3000 });
      if (activeTab === "history") {
        fetchExportHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to export report.", {
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "reports") {
      fetchReport();
    } else {
      fetchExportHistory();
    }
  }, [
    reportType,
    filters,
    currentPage,
    itemsPerPage,
    activeTab,
    historyCurrentPage,
  ]);

  // useEffect(() => {
  //   fetchCorporations();
  // }, []);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleHistoryPageChange = (page) => {
    if (page >= 1 && page <= historyTotalPages) setHistoryCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    setHistoryCurrentPage(1);
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} entries`;
  };

  const getHistoryShowingEntriesText = () => {
    const start = (historyCurrentPage - 1) * itemsPerPage + 1;
    const end = Math.min(
      historyCurrentPage * itemsPerPage,
      historyTotalEntries
    );
    return `Showing ${start} to ${end} of ${historyTotalEntries.toLocaleString()} entries`;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 10 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b border-gray-100">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-2">
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  const reportTypes = [
    { value: "ar_aging", label: "AR Aging" },
    { value: "collection", label: "Collection" },
    { value: "billing_summary", label: "Billing Summary" },
    { value: "interest_billing", label: "Interest Billing" },
    { value: "credit_memos", label: "Credit Memos" },
    { value: "cancelled_billing", label: "Cancelled Billing" },
  ];

  const historyColumns = [
    "Export ID",
    "Report Type",
    "File Name",
    "File Type",
    "File Size (KB)",
    "Status",
    "Created At",
    "Download",
  ];


  const handleRegenerateUrl = async (exportId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/reports/regenerate-url`,
        { export_id: exportId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );
      toast.success("URL regenerated successfully.", { duration: 3000 });
      fetchExportHistory(); // Refresh history to update the URL
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to regenerate URL.", {
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };
  const isUrlExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-600 text-white rounded-lg">
            <FontAwesomeIcon icon={faSearch} className="text-sm" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Reports</h1>
            <p className="text-xs text-gray-500">
              Generate and view billing and collection reports
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveTab("reports")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === "reports"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === "history"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Export History
            </button>
          </div>

          {activeTab === "reports" && (
            <>
              {/* Filters */}
              <div className="p-4 bg-gray-50 border-b border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Report Type
                    </label>
                    <select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                    >
                      {reportTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Corporate
                    </label>
                    <input
                      type="text"
                      placeholder="Search corporate..."
                      value={corporateSearch}
                      onChange={handleCorporateSearch}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                    />
                    {filteredCorporations.length > 0 && (
                      <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                        {filteredCorporations.map((corp) => (
                          <li
                            key={corp.id}
                            onClick={() => handleCorporateSelect(corp)}
                            className="px-3 py-1.5 text-sm hover:bg-indigo-50 cursor-pointer"
                          >
                            {corp.id} - {corp.name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {filters.corporate.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {filters.corporate.map((corp) => (
                          <span
                            key={corp.id}
                            className="inline-flex items-center px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                          >
                            {corp.name}
                            <button
                              onClick={() => handleRemoveCorporate(corp.id)}
                              className="ml-1 text-indigo-600 hover:text-indigo-800"
                            >
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="h-3 w-3"
                              />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Payment Status
                    </label>
                    <select
                      value={filters.paymentStatus}
                      onChange={(e) =>
                        handleFilterChange("paymentStatus", e.target.value)
                      }
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                    >
                      <option value="">All</option>
                      <option value="Paid">Paid</option>
                      <option value="Partially Paid">Partially Paid</option>
                      <option value="Unpaid">Unpaid</option>
                      <option value="Overpaid">Overpaid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Billed Type
                    </label>
                    <select
                      value={filters.billedType}
                      onChange={(e) =>
                        handleFilterChange("billedType", e.target.value)
                      }
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                    >
                      <option value="">All</option>
                      <option value="NAF">Network Access Fee</option>
                      <option value="CRF">Card Replacement Fee</option>
                      <option value="CPF">Card Printing Fee</option>
                      <option value="AF">Admin Fee</option>
                      <option value="IF">Interest Fee</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Billing Date From
                      </label>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) =>
                          handleFilterChange("dateFrom", e.target.value)
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Billing Date To
                      </label>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) =>
                          handleFilterChange("dateTo", e.target.value)
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        {columns.map((column, index) => (
                          <th
                            key={index}
                            className="p-2 text-left font-semibold text-gray-600"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {loading ? (
                        <LoadingSkeleton
                          rows={itemsPerPage}
                          columns={columns.length}
                        />
                      ) : data.length === 0 ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="p-2 text-center text-gray-500"
                          >
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        data.map((row, index) => (
                          // <tr
                          //   key={index}
                          //   className="hover:bg-gray-50 transition"
                          // >
                          //   {Object.values(row).map((value, colIndex) => (
                          //     <td key={colIndex} className="p-2">
                          //       {typeof value === "number" &&
                          //       [
                          //         "billed_amount",
                          //         "amount_paid",
                          //         "cm_amount",
                          //         "cwt",
                          //         "balance",
                          //         "total_billed",
                          //         "total_paid",
                          //         "total_balance",
                          //         "interest_amount",
                          //         "net",
                          //         "vat",
                          //         "gross",
                          //         "transaction_amount",
                          //         // "transaction_mem_count",
                          //       ].includes(Object.keys(row)[colIndex])
                          //         ? phpFormatter.format(value)
                          //         : value || "-"}
                          //     </td>
                          //   ))}
                          // </tr>
                          <tr
                            key={index}
                            className="hover:bg-gray-50 transition"
                          >
                            {Object.values(row).map((value, colIndex) => {
                              const field = Object.keys(row)[colIndex];
                              const isMonetary = [
                                "billed_amount",
                                "amount_paid",
                                "cm_amount",
                                "cwt",
                                "balance",
                                "total_billed",
                                "total_paid",
                                "total_balance",
                                "interest_amount",
                                "net",
                                "vat",
                                "gross",
                                "transaction_amount",
                                "0-30_days",
                                "31-45_days",
                                "46-60_days",
                                "61-90_days",
                                "over_90_days",
                                "grand_total"
                              ].includes(field);
                              return (
                                <td key={colIndex} className="p-2">
                                  {typeof value === "number"
                                    ? (isMonetary
                                        ? (value === 0 ? '-' : phpFormatter.format(value))
                                        : value)
                                    : value || "-"}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination and Actions */}
              <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    {[5, 10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>
                        {val}/page
                      </option>
                    ))}
                  </select>
                  <span>{getShowingEntriesText()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>
                  <span className="text-xs text-gray-600">
                    {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                </div>
                <div className="flex gap-2">
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="csv">CSV</option>
                    <option value="xlsx">Excel</option>
                    <option value="pdf">PDF</option>
                  </select>
                  <button
                    onClick={handleExport}
                    disabled={loading}
                    className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 transition ${
                      loading
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFileExport} /> Export
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === "history" && (
            <>
              {/* History Filters */}
              <div className="p-4 bg-gray-50 border-b border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Report Type
                    </label>
                    <select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                    >
                      <option value="">All</option>
                      {reportTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        From
                      </label>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) =>
                          handleFilterChange("dateFrom", e.target.value)
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        To
                      </label>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) =>
                          handleFilterChange("dateTo", e.target.value)
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* History Table */}
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        {historyColumns.map((column, index) => (
                          <th
                            key={index}
                            className="p-2 text-left font-semibold text-gray-600"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {loading ? (
                        <LoadingSkeleton
                          rows={itemsPerPage}
                          columns={historyColumns.length}
                        />
                      ) : historyData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={historyColumns.length}
                            className="p-2 text-center text-gray-500"
                          >
                            No export history found.
                          </td>
                        </tr>
                      ) : (
                        historyData.map((row, index) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-50 transition"
                          >
                            <td className="p-2">{row.export_id}</td>
                            <td className="p-2">{row.report_type}</td>
                            <td className="p-2">{row.original_file_name}</td>
                            <td className="p-2">
                              {row.file_type.split("/")[1].toUpperCase()}
                            </td>
                            <td className="p-2">
                              {(row.file_size / 1024).toFixed(2)}
                            </td>
                            <td className="p-2 capitalize">{row.status}</td>
                            <td className="p-2">{row.created_at}</td>{" "}
                            <td className="p-2">
                              {isUrlExpired(row.url_expires_at) ? (
                                <button
                                  onClick={() => handleRegenerateUrl(row.export_id)}
                                  disabled={loading}
                                  className="text-indigo-600 hover:text-indigo-800"
                                  title="Regenerate URL"
                                >
                                  <FontAwesomeIcon icon={faSyncAlt} />
                                </button>
                              ) : (
                                <a
                                  href={row.signed_url}
                                  download
                                  className="text-indigo-600 hover:text-indigo-800"
                                  title="Download"
                                >
                                  <FontAwesomeIcon icon={faDownload} />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* History Pagination */}
              <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    {[5, 10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>
                        {val}/page
                      </option>
                    ))}
                  </select>
                  <span>{getHistoryShowingEntriesText()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      handleHistoryPageChange(historyCurrentPage - 1)
                    }
                    disabled={historyCurrentPage === 1 || loading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>
                  <span className="text-xs text-gray-600">
                    {historyCurrentPage} of {historyTotalPages}
                  </span>
                  <button
                    onClick={() =>
                      handleHistoryPageChange(historyCurrentPage + 1)
                    }
                    disabled={
                      historyCurrentPage === historyTotalPages || loading
                    }
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
